import { catchError, tap } from 'rxjs/operators';
import { BehaviorSubject, Observable, of } from 'rxjs';

import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { PHX_STATSIG_KEYS } from '../constants.js';

// Injection tokens
export const ENVIRONMENT_ENV = new InjectionToken<any>('Environment');
export const STATSIG_CLIENT_KEY = new InjectionToken<string>('StatsigClientKey');

// Fallback default configs
const DEFAULT_CONFIGS: Record<PHX_STATSIG_KEYS, boolean> = {
  'virtual-scroll-updates': false,
  'sort-consistency-enabled': false,
  'phx-select-custom-tooltip-enabled': false,
  'phx-select-tooltip-on-truncate-enabled': false,
  'phx-date-picker-toggle-enabled': false,
};

// Statsig config constants
const STATSIG_API_URL = 'https://api.statsig.com/v1/get_config';
const CONFIG_NAME = 'fe-phx-dynamic-config';

// Mirrors the env→tier logic in mt-shared's mapEnvForStatsigConfig (statsig.service.ts).
// Keep in sync if new environments are added there.
const ENV_TIER_MAP: Record<string, string> = {
  local: 'development',
  localPreview: 'preview',
};

@Injectable({ providedIn: 'root' })
export class PhxStatsigService {
  public static phxConfigs$ = new BehaviorSubject<any>(DEFAULT_CONFIGS);
  private static hasLoaded = false;

  constructor(
    private http: HttpClient,
    @Optional() @Inject(ENVIRONMENT_ENV) private env: any, // Marking the Injection Optional, otherwise getting error in demo and storybook as Environment is not getting injected
    @Optional() @Inject(STATSIG_CLIENT_KEY) private statsigClientKey: string
  ) {
    if (!this.statsigClientKey) {
      console.warn(
        '[PhxStatsigService] No STATSIG_CLIENT_KEY provided — skipping Statsig fetch and using DEFAULT_CONFIGS. ' +
        'Provide STATSIG_CLIENT_KEY via DI to enable remote configs.'
      );
    }
    this.getConfigs().subscribe(); // Trigger the loading once on init
  }

  getConfigs(): Observable<any> {
    if (!PhxStatsigService.hasLoaded && this.statsigClientKey) {
      PhxStatsigService.hasLoaded = true;

      const headers = new HttpHeaders({
        'statsig-api-key': this.statsigClientKey,
        'Content-Type': 'application/json',
      });

      const body = {
        configName: CONFIG_NAME,
        user: {
          statsigEnvironment: { tier: ENV_TIER_MAP[this.env] ?? this.env ?? 'preview' },
        },
      };

      this.http
        .post<any>(STATSIG_API_URL, body, { headers })
        .pipe(
          tap(response => {
            if (response?.value) {
              PhxStatsigService.phxConfigs$.next(response.value);
            }
          }),
          catchError(error => {
            console.error('Failed to fetch Statsig config for phx:', error);
            return of(null);
          }),
        )
        .subscribe();
    }

    return PhxStatsigService.phxConfigs$.asObservable();
  }

  getCurrentConfig(): any {
    const value = PhxStatsigService.phxConfigs$.getValue();
    return value || DEFAULT_CONFIGS;
  }
}
